import { LoggedInUser } from '@/entities/auth/loggedInUser';
import { ForgottenPasswordUser } from '@/entities/auth/forgottenPasswordUser';
import sha256 from 'crypto-js/sha256';
import { fullStory, gtm, gtag } from '@/ui/helpers';
import { setUserId, unsetUserId } from '@/services/activityTracker/activityTrackerHelper';
import { isProd } from '@/ui/helpers/systemHelper';
import { RegisteringUser } from '@/entities/auth/registeringUser';
import { Purchase } from '@/entities/analytics';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

const googleTagManagerEnabled = isProd;
const fullStoryEnabled = isProd;
const GOOGLE_ANALYTICS_METHOD_EMAIL = 'email';

export const setUser = (user: LoggedInUser): void => {
  setUserId(user.email);
  if (fullStoryEnabled) {
    fullStory.identify(sha256(user.id.toString()).toString(), {
      displayName: user.name,
      email: user.email,
    });
  }
  if (googleTagManagerEnabled) {
    const hasAnalytics = !!user.analytics;
    const hasLegacySubscription = !!user._legacy_subscription;
    gtm.dataLayer().push({
      event: 'userDataSet',
      userID: user.analytics ? user.analytics.id : sha256(user.id.toString()).toString(),
      userName: user.name,
      userEmail: user.email,
      userLanguage: user.locale ? user.locale : null,
      organizationID: sha256(user.organization.id.toString()).toString(),
      subscriptionPlan: hasLegacySubscription ? user._legacy_subscription!.name : null,
      subscriptionInterval: hasLegacySubscription ? user._legacy_subscription!.interval : null,
      subscriptionStatus: hasLegacySubscription ? user._legacy_subscription!.status : null,
      onFreePlan: hasLegacySubscription && user._legacy_subscription!.is_free,
      completedInspections: hasAnalytics ? user.analytics.completedInspections : 0,
      createdIssues: hasAnalytics ? user.analytics.issuesCreated : 0,
    });
  }
};

export const unsetUser = (): void => {
  unsetUserId();
  if (fullStoryEnabled) {
    fullStory.anonymize();
  }
  if (googleTagManagerEnabled) {
    gtm.dataLayer().push({
      event: 'userDataSet',
      userID: null,
      userName: null,
      userEmail: null,
      userLanguage: null,
      organizationID: null,
      subscriptionPlan: null,
      subscriptionInterval: null,
      subscriptionStatus: null,
      onFreePlan: null,
      completedInspections: null,
      createdIssues: null,
    });
  }
};

export const login = (user: LoggedInUser): void => {
  setUser(user);

  if (googleTagManagerEnabled) {
    gtag.event('login', {
      method: GOOGLE_ANALYTICS_METHOD_EMAIL,
    });
  }
};

export const register = (user: LoggedInUser): void => {
  setUserId(user.email);
  if (googleTagManagerEnabled) {
    gtm.dataLayer().push({
      event: 'formSubmission',
      formType: 'Register',
      userOnboardingType: '',
      userId: user.id,
      userName: user.name,
      userEmail: user.email,
      userPhone: user.phoneNumber,
    });

    gtag.event('sign_up', {
      method: GOOGLE_ANALYTICS_METHOD_EMAIL,
    });
  }
};

export const registerWithPlan = (user: RegisteringUser, plan: Record<string, unknown>): void => {
  if (googleTagManagerEnabled) {
    gtm.dataLayer().push({
      event: 'registerWithPlan',
      formType: 'Register',
      userEmail: user.email,
      userPhone: user.contactPhoneCode + user.contactPhoneNumber,
      planType: plan.plan,
      numberOfUser: plan.quantity,
      interval: plan.interval,
    });
  }
};

/* eslint-disable @typescript-eslint/no-unused-vars */
export const logout = (user: LoggedInUser): void => {
  unsetUser();
};

export const passwordResetInitiated = (user: ForgottenPasswordUser): void => {
  //do we need this method?
};

export const passwordReset = (user: LoggedInUser): void => {
  //do we need this method?
};

export const purchase = (purchaseData: Purchase): void => {
  if (googleTagManagerEnabled) {
    // @ts-ignore
    gtag.purchase({
      transaction_id: purchaseData.id,
      value: purchaseData.total,
      currency: purchaseData.currency.toUpperCase(),
      items: [
        {
          item_id: purchaseData.interval,
          item_name: purchaseData.nickname,
          price: purchaseData.pricePerUser,
          quantity: purchaseData.usersCount,
        }],
    });
  }
};

// create user

// create checklist

// create issue

// create inspection
